import React, { useEffect } from "react";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import { ComponentEnum, ComponentI, LessonI } from "../../../types";
import Input from "../../../componentLibrary/Input";
import Button from "../../../componentLibrary/Button";
import Checkbox from "../../../componentLibrary/Checkbox";
import { useGetPageContainedLessons } from "../../../api/queries";
import { SelectedComponentType } from "../utils/selectedComponentUtils";
import Combobox from "../../../componentLibrary/Combobox";
import MultiSelectDropdown, {
  MultiSelectOptionI,
} from "../../../componentLibrary/MultiSelectDropdown";
import {
  getNewComponentContent,
  transferContentVariables,
} from "../utils/addComponent";
import { COMPONENT_GROUPS, getComponentGroup } from "../utils/componentGroup";
import DraggableComponentCard from "./DraggableComponentCard";
import AddVariableModal from "./AddVariableModal";

export type PageComponentContainerMode = "page-info" | "components" | "all";

const PageComponentContainer = ({
  lesson,
  setLesson,
  currentPageId,
  selectedComponent,
  handleComponentClick,
  variableUptoCurrentPage,
  variableUptoPreviousPage,
  setWarnings,
  setLessonChanged,
  componentRefs,
  mode = "all",
  minimizedComponents: externalMinimizedComponents,
  setMinimizedComponents: externalSetMinimizedComponents,
}: {
  lesson: LessonI;
  setLesson: React.Dispatch<React.SetStateAction<LessonI>>;
  currentPageId: string;
  selectedComponent: SelectedComponentType;
  handleComponentClick: (selectedComponent: SelectedComponentType) => void;
  variableUptoCurrentPage: string[];
  variableUptoPreviousPage: string[];
  setWarnings: React.Dispatch<React.SetStateAction<string[]>>;
  setLessonChanged?: React.Dispatch<React.SetStateAction<boolean>>;
  componentRefs: React.RefObject<{ [key: string]: HTMLDivElement | null }>;
  mode?: PageComponentContainerMode;
  minimizedComponents?: { [key: string]: boolean };
  setMinimizedComponents?: React.Dispatch<
    React.SetStateAction<{ [key: string]: boolean }>
  >;
}) => {
  const [isCopyModalOpen, setisCopyModalOpen] = React.useState(false);
  const closeModal = () => setisCopyModalOpen(false);
  const openModal = () => setisCopyModalOpen(true);

  const handleFrozenVariablesChange = (value: MultiSelectOptionI<string>[]) => {
    setLessonChanged?.(true);
    setLesson((prevLesson) => {
      const newPages = prevLesson.pages.map((page) =>
        page._id === currentPageId
          ? {
              ...page,
              freezeVariablesFromPreviousPage: value.map((v) => v.value),
            }
          : page,
      );
      return { ...prevLesson, pages: newPages };
    });
  };

  const [internalMinimizedComponents, setInternalMinimizedComponents] =
    React.useState<{ [key: string]: boolean }>({});
  const isControlled =
    externalMinimizedComponents !== undefined &&
    externalSetMinimizedComponents !== undefined;
  const minimizedComponents = isControlled
    ? externalMinimizedComponents
    : internalMinimizedComponents;
  const setMinimizedComponents = isControlled
    ? externalSetMinimizedComponents
    : setInternalMinimizedComponents;
  const getIsMinimized = (componentId: string) => {
    return minimizedComponents[componentId] ?? false;
  };
  const setIsMinimized = (componentId: string, isMinimized: boolean) => {
    setMinimizedComponents((prev) => ({ ...prev, [componentId]: isMinimized }));
  };

  const { data: containingLessons = [] } =
    useGetPageContainedLessons(currentPageId);
  // Find current page and its index
  const currentPageIndex = lesson.pages.findIndex(
    (page) => page._id === currentPageId,
  );
  const currentPage = lesson.pages[currentPageIndex] ?? lesson.pages[0]; // Default to first page if not found

  const selectedVariables: MultiSelectOptionI<string>[] = (
    currentPage.freezeVariablesFromPreviousPage ?? []
  ).map((variable) => ({
    value: variable,
    label: variable,
  }));

  // Get all key takeaways from all pages in the lesson (excluding current page)
  const allLessonKeyTakeaways = React.useMemo(() => {
    const takeaways = new Set<string>();
    lesson.pages.forEach((page) => {
      if (page.keyTakeaways) {
        takeaways.add(page.keyTakeaways);
      }
    });
    return Array.from(takeaways);
  }, [lesson.pages]);

  const handlePageTitleChange = (title: string) => {
    setLessonChanged?.(true);
    setLesson((prevLesson) => {
      const newPages = prevLesson.pages.map((page) =>
        page._id === currentPageId ? { ...page, title } : page,
      );
      return { ...prevLesson, pages: newPages };
    });
  };

  const handleIsToolboxToolChange = () => {
    setLessonChanged?.(true);
    setLesson((prevLesson) => {
      const newPages = prevLesson.pages.map((page) =>
        page._id === currentPageId
          ? { ...page, isToolboxTool: !page.isToolboxTool }
          : page,
      );
      return { ...prevLesson, pages: newPages };
    });
  };

  const handleKeyTakeawaysChange = (value: {
    value: string;
    label: string;
  }) => {
    setLessonChanged?.(true);
    setLesson((prevLesson) => {
      const newPages = prevLesson.pages.map((page) =>
        page._id === currentPageId
          ? { ...page, keyTakeaways: value.value }
          : page,
      );
      return { ...prevLesson, pages: newPages };
    });
  };

  const handleCreateKeyTakeaway = (inputValue: string) => {
    setLessonChanged?.(true);
    setLesson((prevLesson) => {
      const newPages = prevLesson.pages.map((page) =>
        page._id === currentPageId
          ? { ...page, keyTakeaways: inputValue }
          : page,
      );
      return { ...prevLesson, pages: newPages };
    });
  };

  const removeComponent = (componentId: string) => {
    setLessonChanged?.(true);
    setLesson((prevLesson) => {
      const newComponents = currentPage.components.filter(
        (component) => component._id !== componentId,
      );
      const newPages = prevLesson.pages.map((page) =>
        page._id === currentPageId
          ? { ...page, components: newComponents }
          : page,
      );
      return { ...prevLesson, pages: newPages };
    });
  };

  const checkDuplicateVariables = (
    setWarnings: React.Dispatch<React.SetStateAction<string[]>>,
    updatedLesson: LessonI,
  ) => {
    const newWarnings: string[] = [];
    const pageFrequency = new Map<string, Map<string, number>>();

    // Build mapping of variables to page titles and frequencies
    updatedLesson.pages.forEach((page) => {
      const checkComponent = (component: ComponentI) => {
        if (component.content?.variable) {
          const variableName = component.content.variable;
          if (!pageFrequency.has(variableName)) {
            pageFrequency.set(variableName, new Map());
          }
          const frequency = pageFrequency.get(variableName)!;
          frequency.set(page.title, (frequency.get(page.title) || 0) + 1);
        }
        // Recursively check components if this is a logic component
        if (
          component.type === ComponentEnum.LOGIC_COMPONENT &&
          component.content?.componentsList
        ) {
          component.content.componentsList.forEach(checkComponent);
        }
      };

      page.components.forEach(checkComponent);
    });
    // Get variables in current page
    const currentPageVariables = new Set<string>();
    const currentPage1 =
      updatedLesson.pages[currentPageIndex] ?? updatedLesson.pages[0];
    currentPage1.components.forEach((component) => {
      if (component.content?.variable) {
        currentPageVariables.add(component.content.variable);
      }
      // Check nested components in logic components
      if (
        component.type === ComponentEnum.LOGIC_COMPONENT &&
        component.content?.components
      ) {
        component.content.components.forEach((nestedComponent) => {
          if (nestedComponent.content?.variable) {
            currentPageVariables.add(nestedComponent.content.variable);
          }
        });
      }
    });

    currentPageVariables.forEach((variable) => {
      const pagesWithVariable = pageFrequency.get(variable);
      if (pagesWithVariable) {
        const totalCount = Array.from(pagesWithVariable.values()).reduce(
          (sum, count) => sum + count,
          0,
        );
        if (totalCount > 1) {
          const pageList = Array.from(pagesWithVariable.entries())
            .map(([pageTitle, count]) => `${pageTitle} (${count})`)
            .join(", ");
          newWarnings.push(
            `Variable "${variable}" is used ${totalCount} times across pages: ${pageList}`,
          );
        }
      }
    });

    setWarnings(newWarnings);
  };

  useEffect(() => {
    checkDuplicateVariables(setWarnings, lesson);
  }, [currentPageIndex]);

  const handleContentChange = (
    componentId: string,
    field: string,
    value: string | undefined,
  ) => {
    setLessonChanged?.(true);
    setLesson((prevLesson) => {
      const newPages = prevLesson.pages.map((page) => {
        if (page._id === currentPageId) {
          const newComponents = page.components.map((component) => {
            if (component._id === componentId) {
              const updatedContent = { ...component.content };

              if (value === undefined) {
                delete updatedContent[field];
              } else {
                updatedContent[field] = value;
              }

              return { ...component, content: updatedContent };
            }
            return component;
          });
          return { ...page, components: newComponents };
        }
        return page;
      });
      const updatedLesson1 = { ...prevLesson, pages: newPages };
      if (field === "variable" || field === "componentsList") {
        checkDuplicateVariables(setWarnings, updatedLesson1);
      }
      return updatedLesson1;
    });
  };

  const handleTypeChange = (componentId: string, value: ComponentEnum) => {
    setLessonChanged?.(true);
    setLesson((prevLesson) => {
      const newPages = prevLesson.pages.map((page) => {
        if (page._id === currentPageId) {
          const newComponents = page.components.map((component) => {
            if (component._id === componentId) {
              const newContent = getNewComponentContent(value);

              // Transfer variables from old content to new content based on COMPONENT_GROUPS
              const transferredContent = transferContentVariables(
                component.content,
                newContent,
                component.type,
                value,
                COMPONENT_GROUPS,
              );
              return {
                ...component,
                type: value,
                content: transferredContent,
              };
            }
            return component;
          });
          return { ...page, components: newComponents };
        }
        return page;
      });
      const updatedLesson1 = { ...prevLesson, pages: newPages };
      return updatedLesson1;
    });
  };

  const onDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;

    const newComponents = Array.from(currentPage.components);
    const [removed] = newComponents.splice(source.index, 1);
    newComponents.splice(destination.index, 0, removed);

    const newPages = lesson.pages.map((page) =>
      page._id === currentPageId
        ? { ...page, components: newComponents }
        : page,
    );
    setLesson({ ...lesson, pages: newPages });
  };

  const filteredContainingLessons = containingLessons.filter(
    (l) => l._id !== lesson._id,
  );

  const showPageInfo = mode === "page-info" || mode === "all";
  const showComponents = mode === "components" || mode === "all";

  return (
    <>
      {showPageInfo && (
        <>
          <h3 className="mb-2">
            Page{" "}
            {lesson.pages.findIndex((page) => page._id === currentPageId) + 1}
          </h3>
          <Input
            placeholder="Page Title"
            value={currentPage.title}
            onChange={(e) => handlePageTitleChange(e.target.value)}
          />
          <div className="mt-3">
            <Checkbox
              label="Show in topic toolbox (My Tools)"
              checked={Boolean(currentPage.isToolboxTool)}
              onChange={handleIsToolboxToolChange}
            />
            <p className="text-xs text-gray-500 mt-1 ml-7">
              Learners see this page as a shortcut in the toolbox for this
              topic.
            </p>
          </div>
          <div className="mt-4">
            <Combobox
              label="Key Takeaways"
              options={allLessonKeyTakeaways.map((takeaway) => ({
                value: takeaway,
                label: takeaway,
              }))}
              value={
                currentPage.keyTakeaways
                  ? {
                      value: currentPage.keyTakeaways,
                      label: currentPage.keyTakeaways,
                    }
                  : {
                      value: "",
                      label: "",
                    }
              }
              onChange={handleKeyTakeawaysChange}
              onCreateOption={handleCreateKeyTakeaway}
              placeholder="Add key takeaway..."
            />
          </div>
          <div className="mt-4">
            <MultiSelectDropdown<string>
              label="Freeze Variables from previous page"
              options={variableUptoPreviousPage.map((variable) => ({
                value: variable,
                label: variable,
              }))}
              value={selectedVariables}
              onChange={handleFrozenVariablesChange}
              placeholder="Select variables..."
            />
          </div>
          {filteredContainingLessons.length > 0 && (
            <div className="mt-2 text-sm text-red-600">
              This page is also used in:{" "}
              {filteredContainingLessons.map((l) => l.title).join(" | ")}
            </div>
          )}
        </>
      )}
      {showComponents && (
        <>
          <div
            className={`${showPageInfo ? "mt-3" : ""} flex justify-between align-middle`}
          >
            <h3>Components</h3>
            <Button variant="dark" onClick={openModal}>
              Add Variable
            </Button>
          </div>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="steps" direction="vertical">
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="space-y-2"
                >
                  {currentPage.components.map((component, index) => (
                    <div
                      key={component._id}
                      ref={(el) => (componentRefs.current[component._id] = el)}
                    >
                      <DraggableComponentCard
                        component={component}
                        index={index}
                        handleContentChange={handleContentChange}
                        onRemove={() => removeComponent(component._id)}
                        variableUptoCurrentPage={variableUptoCurrentPage}
                        handleComponentClick={handleComponentClick}
                        selectedComponent={selectedComponent}
                        componentRefs={componentRefs}
                        handleTypeChange={handleTypeChange}
                        componentGroup={getComponentGroup(component.type)}
                        isMinimized={getIsMinimized(component._id)}
                        setIsMinimized={(isMinimized: boolean) =>
                          setIsMinimized(component._id, isMinimized)
                        }
                      />
                    </div>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
          <AddVariableModal
            lesson={lesson}
            currentPageId={currentPageId}
            isModalOpen={isCopyModalOpen}
            closeModal={closeModal}
          />
        </>
      )}
    </>
  );
};

export default PageComponentContainer;
